import Check from "@mui/icons-material/Check";
import {
  Alert,
  Box,
  Button,
  Divider,
  GridLegacy as Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ConfirmFacilityChanges } from "components/Credentials/ConfirmFacilityChanges";
import { CredentialFacilitiesGrid } from "components/Credentials/CredentialFacilitiesGrid";
import { CredentialFields } from "components/Credentials/CredentialFields";
import { CustomerFacilityPicker } from "components/Credentials/CustomerFacilityPicker";
import { StaffFacilityPicker } from "components/Credentials/StaffFacilityPicker";
import { CredentialSurface, useCredentialEditor } from "components/Credentials/useCredentialEditor";
import { Dialog } from "components/Dialog";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { SkeletonFormGroup } from "components/Skeleton";
import { InventoryOverviewQuery } from "generated-graphql/graphql";
import { useCallback, useState } from "react";
import { FormProvider } from "react-hook-form";
import { CredentialVerification } from "routes/Staff/Fulfillment/FulfillmentDetails/CredentialVerification";
import { hasCriticalIssues } from "util/forms";

/** Read-only counterpart to a form field: label and value on one line. */
function ReadOnlyField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

/**
 * The subtitle rides in the title rather than the `subheader` slot, which renders a bold
 * `subtitle1` down in the content area with its own spacing above the first field.
 */
function DialogHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Stack spacing={0} sx={{ flex: 1, minWidth: 0, paddingRight: 1 }}>
      <Typography variant="h6">{title}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Stack>
  );
}

/** Holds the dialog's two-column width while it loads, so opening doesn't reflow. */
function CredentialDialogSkeleton() {
  const theme = useTheme();
  return (
    <Grid container sx={{ minWidth: 1200 }}>
      <Grid item xs={12} sm={4} paddingRight={2}>
        <SkeletonFormGroup count={4} gridSx={{ p: 0 }} />
      </Grid>
      <Grid item xs={12} sm={8} paddingLeft={2}>
        <Skeleton variant="rounded" sx={{ mb: theme.spacing(1) }} height={350} />
      </Grid>
    </Grid>
  );
}

function CredentialAlerts({
  duplicateExistingId,
  onOpenExisting,
}: {
  duplicateExistingId: string | null;
  onOpenExisting?: (id: string) => void;
}) {
  return (
    <>
      {duplicateExistingId && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => onOpenExisting?.(duplicateExistingId)}
            >
              Open it
            </Button>
          }
        >
          That portal and username are already taken by another credential.
        </Alert>
      )}
    </>
  );
}

interface CredentialDialogProps {
  onClose: () => void;
  mode?: "add" | "edit";
  credentialId?: string;
  currentFacility?: InventoryOverviewQuery["facility"];
  /**
   * Which page is hosting this. `staff` swaps the read, the write and the facility
   * picker for their cross-tenant equivalents; everything the user sees is the same.
   */
  surface?: CredentialSurface;
  /**
   * Overrides the tenant derived from `useTenant()`. The staff EPCRA drawer is
   * cross-tenant (not under an `/o/{tenantId}` route), so it passes the row's tenant
   * explicitly — otherwise the upsert would write a stale session tenant onto the
   * credential.
   */
  tenantId?: string;
  /**
   * Offer the Encamp-owned checkbox. Off on the customer credentials page — a
   * customer-facing credential always has a tenant and is never Encamp-owned — and on
   * for both staff surfaces. Still `isStaff`-gated underneath either way.
   */
  showOwnerControl?: boolean;
  /** Staff only: jumps to the credential already holding a duplicate (portal, username). */
  onOpenExisting?: (id: string) => void;
}

export const CredentialDialog: React.FC<CredentialDialogProps> = ({
  onClose,
  mode = "edit",
  credentialId,
  currentFacility,
  surface = "customer",
  tenantId,
  showOwnerControl = false,
  onOpenExisting,
}) => {
  const editor = useCredentialEditor({
    surface,
    mode,
    credentialId,
    tenantId,
    currentFacility,
    onSaved: onClose,
  });
  const { form } = editor;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmingChanges, setConfirmingChanges] = useState(false);

  const isStaffSurface = surface === "staff";
  const addingCredential = mode === "add";
  const savingOrLoading = editor.saving || editor.loading;
  const fieldsDisabled = savingOrLoading || editor.readOnly;

  const onSubmit = useCallback(async () => {
    if (!(await editor.validateForSubmit())) return;

    // Only facility changes get a summary; a rename shouldn't need confirming.
    if (editor.pendingCount > 0) {
      setConfirmingChanges(true);
      return;
    }
    await editor.write();
  }, [editor]);

  // On failure the summary closes so the error isn't behind it, and the staged changes
  // survive for another attempt. On success the whole dialog unmounts.
  const onConfirmChanges = useCallback(async () => {
    const saved = await editor.write();
    if (!saved) setConfirmingChanges(false);
  }, [editor]);

  let dialogTitle = "Edit Credential";
  if (editor.readOnly) dialogTitle = "View Credential";
  else if (addingCredential) dialogTitle = "Add Credential";

  const showSkeleton = editor.loading && !editor.hasLoadedOnce;

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="xl"
        title={<DialogHeading title={dialogTitle} subtitle={editor.subtitle || undefined} />}
        primaryActionButtonProps={{
          loading: savingOrLoading,
          onClick: form.handleSubmit(onSubmit),
          disabled: hasCriticalIssues(editor.issues) || editor.readOnly,
        }}
      >
        {showSkeleton && <CredentialDialogSkeleton />}

        {editor.notFound && !showSkeleton && (
          <Typography color="text.secondary">Credential not found.</Typography>
        )}

        {!showSkeleton && !editor.notFound && (
          <FormProvider {...form}>
            <CredentialAlerts
              duplicateExistingId={editor.duplicateExistingId}
              onOpenExisting={onOpenExisting}
            />
            <CredentialDialogBody
              editor={editor}
              isStaffSurface={isStaffSurface}
              fieldsDisabled={fieldsDisabled}
              savingOrLoading={savingOrLoading}
              showOwnerControl={showOwnerControl}
              onSubmit={onSubmit}
              onOpenPicker={() => setPickerOpen(true)}
            />
          </FormProvider>
        )}
      </Dialog>

      <FacilityPicker
        isStaffSurface={isStaffSurface}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        editor={editor}
      />

      {confirmingChanges && (
        <ConfirmFacilityChanges
          adds={editor.pendingAdds}
          removes={editor.stagedRemovals}
          loading={editor.saving}
          onConfirm={onConfirmChanges}
          onClose={() => setConfirmingChanges(false)}
        />
      )}
    </>
  );
};

/** Fields left, facilities right — the same two columns on both surfaces. */
function CredentialDialogBody({
  editor,
  isStaffSurface,
  fieldsDisabled,
  savingOrLoading,
  showOwnerControl,
  onSubmit,
  onOpenPicker,
}: {
  editor: ReturnType<typeof useCredentialEditor>;
  isStaffSurface: boolean;
  fieldsDisabled: boolean;
  savingOrLoading: boolean;
  showOwnerControl: boolean;
  onSubmit: () => Promise<void>;
  onOpenPicker: () => void;
}) {
  const theme = useTheme();
  const { form, isStaff } = editor;

  return (
    <Box
      component="form"
      onSubmit={form.handleSubmit(onSubmit)}
      sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 0.5 }}
    >
      <Grid container>
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          spacing={theme.spacing(3)}
        >
          <Grid item xs={12} sm={4}>
            <CredentialFields
              control={form.control}
              kind={editor.watchedKind}
              disabled={fieldsDisabled}
              busy={savingOrLoading}
              mode={editor.passwordMode}
              passwordHelperText={editor.passwordHelperText}
              canViewCredentials={editor.canViewCredentials}
              showPasswordCopyIcon={editor.showPasswordCopyIcon}
              passwordCopyValue={editor.passwordCopyValue}
              storedPortalId={editor.storedPortalId}
              onPasswordEdit={editor.onPasswordEdit}
              onPasswordCancelEdit={editor.onPasswordCancelEdit}
              usernameCopyValue={form.getValues("username")}
              emailCopyValue={form.getValues("email")}
              selectedPortalJurisdiction={editor.selectedPortalJurisdiction}
              onJurisdictionChange={editor.setSelectedPortalJurisdiction}
              showOwnerControl={showOwnerControl && isStaff}
              tenancy={editor.tenancy}
              showSecurityQuestions={isStaff}
              // The three `uses*` flags aren't on `CredentialForStaffInput`, so the
              // staff surface can't persist them.
              showPortalFlags={isStaff && !isStaffSurface}
              verification={
                <CredentialVerificationSlot
                  isStaffSurface={isStaffSurface}
                  isStaff={isStaff}
                  editor={editor}
                />
              }
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <CredentialFacilitiesGrid
              rows={editor.facilityRows}
              loading={editor.facilitiesLoading}
              disabled={fieldsDisabled}
              showOrganization={isStaffSurface}
              pendingCount={editor.pendingCount}
              onAdd={onOpenPicker}
              onToggle={editor.toggleFacility}
              emptyMessage="No facilities are linked to this credential."
            />
            <ErrorDisplay sx={{ mt: 1 }} fields={["facilities"]} errors={form.formState.errors} />
          </Grid>
        </Stack>
      </Grid>
    </Box>
  );
}

/**
 * Two pickers, because the selection semantics differ rather than the styling: the staff one
 * is cross-tenant and adds-only, the customer one is tenant-scoped and returns a full
 * replacement set. Removals happen in the grid on both.
 */
function FacilityPicker({
  isStaffSurface,
  open,
  onClose,
  editor,
}: {
  isStaffSurface: boolean;
  open: boolean;
  onClose: () => void;
  editor: ReturnType<typeof useCredentialEditor>;
}) {
  // Mounted only while open: kept mounted it held its search, tenant filter and selection
  // across closes, offering a stale selection as though it were fresh.
  if (isStaffSurface) {
    if (!open) return null;
    return (
      <StaffFacilityPicker
        open
        linkedFacilityIds={editor.linkedFacilityIds}
        onSave={(rows) => {
          editor.stageStaffAdds(rows);
          onClose();
        }}
        onCancel={onClose}
      />
    );
  }

  return (
    <CustomerFacilityPicker
      key={editor.customerFacilityIds.join(",")}
      open={open}
      onCancel={onClose}
      onSave={(selectedFacilityIds) => {
        editor.applyCustomerSelection(selectedFacilityIds);
        onClose();
      }}
      initialSelectedIds={editor.customerFacilityIds}
      portalJurisdiction={editor.selectedPortalJurisdiction}
      tenantId={editor.tenantId}
    />
  );
}

/**
 * The customer document carries the fields `CredentialVerification` needs.
 * Staff reuses the same Verify control so unlinked and shared credentials can
 * be verified with only a credentialId.
 */
function CredentialVerificationSlot({
  isStaffSurface,
  isStaff,
  editor,
}: {
  isStaffSurface: boolean;
  isStaff: boolean;
  editor: ReturnType<typeof useCredentialEditor>;
}) {
  if (!isStaff) return null;

  if (isStaffSurface) {
    const credential = editor.staffCredential;
    return (
      <Box sx={{ mt: 2 }}>
        <CredentialVerification
          credential={credential}
          credentialVerificationAvailable={credential?.portalVerifiable ?? false}
          actionAlign="flex-start"
        >
          <ReadOnlyField label="Currently in use">
            {credential?.currentlyInUse ? (
              <Check fontSize="small" />
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </ReadOnlyField>
        </CredentialVerification>
      </Box>
    );
  }

  const credential = editor.customerCredential;
  // The associated facilities are assumed to share one portal jurisdiction, so any one
  // of them can carry the verification check. Unlinked credentials verify with
  // credentialId only.
  const anAssociatedFacility = credential?.facilities?.[0];

  // No status row of our own: `CredentialVerification` already reports the status,
  // alongside the check times and the Verify action.
  return (
    <Box sx={{ mt: 2 }}>
      <CredentialVerification
        credential={credential}
        facilityId={anAssociatedFacility?.id}
        credentialVerificationAvailable={credential?.portal?.verifiable ?? false}
        actionAlign="flex-start"
      />
    </Box>
  );
}
